// Loads the help-center content from /public/help/<locale>/, generated by
// `rake help_center:build`. The bundle ships zero markdown — content is pulled
// once per locale on first modal open (browser-cached via ETag, module-cached
// for the rest of the page lifetime). Untranslated locales fall back to FR.

const FALLBACK_LOCALE = 'fr'

const indexCache = new Map() // locale -> { tree, articles }
const indexPromiseCache = new Map() // locale -> Promise (for in-flight fetches)
const bodyCache = new Map() // `${locale}:${slug}` -> body string
const bodyPromiseCache = new Map()

const fetchJsonWithFallback = async (locale, suffix) => {
  let res = await fetch(`/help/${locale}/${suffix}`, { credentials: 'same-origin' })
  if (res.status === 404 && locale !== FALLBACK_LOCALE) {
    res = await fetch(`/help/${FALLBACK_LOCALE}/${suffix}`, { credentials: 'same-origin' })
  }
  if (!res.ok) throw new Error(`Help content fetch failed: ${suffix} (${res.status})`)
  return res.json()
}

const buildFromTree = tree => {
  const articles = {}
  tree.forEach(chapter => {
    chapter.articles.forEach(article => {
      articles[article.slug] = article
    })
  })
  return { tree, articles }
}

export const loadIndex = locale => {
  if (indexCache.has(locale)) return Promise.resolve(indexCache.get(locale))
  if (indexPromiseCache.has(locale)) return indexPromiseCache.get(locale)

  const promise = fetchJsonWithFallback(locale, 'index.json')
    .then(data => {
      const built = buildFromTree(data.tree)
      indexCache.set(locale, built)
      indexPromiseCache.delete(locale)
      return built
    })
    .catch(err => {
      indexPromiseCache.delete(locale)
      throw err
    })

  indexPromiseCache.set(locale, promise)
  return promise
}

export const loadArticleBody = (locale, slug) => {
  const key = `${locale}:${slug}`
  if (bodyCache.has(key)) return Promise.resolve(bodyCache.get(key))
  if (bodyPromiseCache.has(key)) return bodyPromiseCache.get(key)

  const promise = fetchJsonWithFallback(locale, `articles/${slug}.json`)
    .then(data => {
      bodyCache.set(key, data.body)
      bodyPromiseCache.delete(key)
      return data.body
    })
    .catch(err => {
      bodyPromiseCache.delete(key)
      throw err
    })

  bodyPromiseCache.set(key, promise)
  return promise
}

// Lightweight scoring search. Title >> tags >> intro >> body.
// Operates on an already-loaded `articles` map from loadIndex().
export const searchArticles = (query, articles, limit = 30) => {
  const q = (query || '').trim().toLowerCase()
  if (q.length < 2 || !articles) return []

  const tokens = q.split(/\s+/).filter(Boolean)
  const scored = []

  Object.values(articles).forEach(article => {
    const titleLc = article.title.toLowerCase()
    const introLc = (article.intro || '').toLowerCase()
    const tagsLc  = (article.tags || []).join(' ').toLowerCase()
    const bodyLc  = (article.plain || '').toLowerCase()

    let score = 0
    let firstHit = -1

    tokens.forEach(t => {
      if (titleLc.includes(t)) score += 100
      if (titleLc.startsWith(t)) score += 50
      if (tagsLc.includes(t))   score += 40
      if (introLc.includes(t))  score += 25
      const idx = bodyLc.indexOf(t)
      if (idx >= 0) {
        score += 5
        if (firstHit === -1) firstHit = idx
      }
    })

    if (titleLc.includes(q)) score += 60
    if (bodyLc.includes(q))  score += 15

    if (score > 0) {
      let snippet = article.intro || ''
      if (!snippet && firstHit >= 0) {
        const start = Math.max(0, firstHit - 60)
        const end   = Math.min(bodyLc.length, firstHit + 120)
        snippet = (start > 0 ? '… ' : '') + article.plain.slice(start, end) + (end < article.plain.length ? ' …' : '')
      }
      scored.push({ article, score, snippet })
    }
  })

  return scored.sort((a, b) => b.score - a.score).slice(0, limit)
}
